import { useEffect, useMemo, useState } from 'react';
import styles from './PostPopup.module.css';
import { createPost, uploadImage } from '../services/postService';
import { compressImageFile } from '../utils/imageCompression';
import { useTranslation } from 'react-i18next';

const DEFAULT_ROLE = 'user';
const MAX_POST_LENGTH = 500;

const PostPopup = ({
  isOpen,
  onClose,
  userId,
  username,
  authToken = '',
  userRole = DEFAULT_ROLE,
  userPfp = '',
  mountainOptions = [],
  gearOptions = [],
  onPostCreated
}) => {
  const [postText, setPostText] = useState('');
  const [file, setFile] = useState(null);
  const [selectedMountainIds, setSelectedMountainIds] = useState([]);
  const [selectedGearIds, setSelectedGearIds] = useState([]);
  
  // Tagging State
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(null);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [suggestions, setSuggestions] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) {
      setPostText('');
      setFile(null);
      setError('');
      setSelectedMountainIds([]);
      setSelectedGearIds([]);
      setMentionQuery(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const normalizedMountains = useMemo(
    () =>
      (Array.isArray(mountainOptions) ? mountainOptions : []).map((mountain) => {
        const id = String(mountain.mountId ?? mountain.id ?? mountain.slug ?? '').trim();
        return {
          id,
          name:
            mountain.mountName ||
            mountain.name ||
            mountain.title ||
            mountain.slug ||
            t('postPopup.tags.unnamedMountain')
        };
      }).filter((mountain) => mountain.id),
    [mountainOptions, t]
  );

  const normalizedGear = useMemo(
    () =>
      (Array.isArray(gearOptions) ? gearOptions : []).map((gear) => {
        const id = String(gear.id ?? gear.slug ?? gear.itemId ?? '').trim();
        const baseName = gear.name || gear.title || gear.slug || t('postPopup.tags.unnamedGear');
        const label = gear.brand ? `${gear.brand} ${baseName}`.trim() : baseName;
        return { id, name: label };
      }).filter((gear) => gear.id),
    [gearOptions, t]
  );

  if (!isOpen) {
    return null;
  }

  const mapTags = (selectedIds, source) =>
    selectedIds
      .map((id) => source.find((item) => item.id === id))
      .filter(Boolean)
      .map((item) => ({ id: item.id, name: item.name }));

  // Filter out any IDs that are no longer in the text
  const cleanTags = (ids, source) => {
      const currentText = postText;
      return ids.filter(id => {
          const item = source.find(i => i.id === id);
          // Simple check: is '#Name' in text?
          // Note: This isn't perfect for duplicates but good enough for MVP
          return item && currentText.includes(`#${item.name.replace(/\s/g, '')}`);
      });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!userId) {
      setError(t('postPopup.errors.signin'));
      return;
    }

    if (!postText.trim()) {
      setError(t('postPopup.errors.bodyRequired'));
      return;
    }

    if (postText.length > MAX_POST_LENGTH) {
      setError(t('postPopup.errors.bodyTooLong', { max: MAX_POST_LENGTH }));
      return;
    }

    setSubmitting(true);
    try {
      let uploadResult = null;
      if (file) {
        uploadResult = await uploadImage(file, authToken);
      }
      
      const validMountainIds = cleanTags(selectedMountainIds, normalizedMountains);
      const validGearIds = cleanTags(selectedGearIds, normalizedGear);
      
      const mountainTags = mapTags(validMountainIds, normalizedMountains);
      const gearTags = mapTags(validGearIds, normalizedGear);
      
      await createPost({
        userId,
        postText,
        imageUrl: uploadResult?.downloadUrl || '',
        imagePath: uploadResult?.storagePath || '',
        mountainTags,
        gearTags,
        username,
        role: userRole || DEFAULT_ROLE,
        pfp: userPfp || ''
      });
      setPostText('');
      setFile(null);
      setSelectedMountainIds([]);
      setSelectedGearIds([]);
      onPostCreated?.();
      onClose?.();
    } catch (err) {
      setError(err.message || t('postPopup.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }

    try {
      const compressed = await compressImageFile(selectedFile);
      setFile(compressed);
    } catch (compressionError) {
      console.warn('Unable to compress image before upload.', compressionError);
      setFile(selectedFile);
    }
  };

  // Tagging Logic
  const handleTextChange = (e) => {
    const val = e.target.value;
    if (val.length > MAX_POST_LENGTH) return;
    setPostText(val);

    const selectionEnd = e.target.selectionEnd;
    
    // Find the word being typed
    let start = selectionEnd - 1;
    while (start >= 0 && !/\s/.test(val[start])) {
      start--;
    }
    start++; // Start of current word

    const word = val.slice(start, selectionEnd);
    if (word.startsWith('#')) {
      const query = word.slice(1).toLowerCase();
      setMentionQuery(query);
      setMentionIndex(start);
      setActiveSuggestion(0);
      
      // Filter suggestions
      const mMatches = normalizedMountains
        .filter(m => m.name.toLowerCase().includes(query))
        .map(m => ({ ...m, type: 'mountain' }))
        .slice(0, 5);
        
      const gMatches = normalizedGear
        .filter(g => g.name.toLowerCase().includes(query))
        .map(g => ({ ...g, type: 'gear' }))
        .slice(0, 5);
        
      setSuggestions([...mMatches, ...gMatches]);
    } else {
      setMentionQuery(null);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (item) => {
    if (mentionIndex === null) return;
    
    const before = postText.slice(0, mentionIndex);
    // Find where the current word ends (next space or end of string)
    let end = mentionIndex;
    while (end < postText.length && !/\s/.test(postText[end])) {
      end++;
    }
    const after = postText.slice(end);
    
    const tagName = item.name.replace(/\s/g, ''); // Remove spaces for hashtag
    const newText = `${before}#${tagName} ${after}`;
    
    setPostText(newText);
    
    // Add to selected IDs
    if (item.type === 'mountain') {
        setSelectedMountainIds(prev => [...new Set([...prev, item.id])]);
    } else {
        setSelectedGearIds(prev => [...new Set([...prev, item.id])]);
    }
    
    setMentionQuery(null);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
      if (mentionQuery !== null && suggestions.length > 0) {
          if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveSuggestion(prev => (prev + 1) % suggestions.length);
          } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveSuggestion(prev => (prev - 1 + suggestions.length) % suggestions.length);
          } else if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              handleSuggestionClick(suggestions[activeSuggestion]);
          } else if (e.key === 'Escape') {
              setMentionQuery(null);
          }
      }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div className={styles.popupOverlay} onClick={handleOverlayClick}>
      <form className={styles.popupContent} onSubmit={handleSubmit}>
        <h2 className={styles.popupTitle}>{t('postPopup.title')}</h2>

        <div className={styles.formGroup} style={{ position: 'relative' }}>
          <label htmlFor="postText" className={styles.label}>
            {t('postPopup.fields.body')}
          </label>
          <textarea
            id="postText"
            className={styles.textarea}
            placeholder={t('postPopup.fields.bodyPlaceholder')}
            value={postText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            disabled={submitting}
          />
          <div className={styles.charCount} aria-live="polite">
            {postText.length}/{MAX_POST_LENGTH}
          </div>
          
          {mentionQuery !== null && suggestions.length > 0 && (
              <div className={styles.suggestionsList}>
                  <div className={styles.suggestionHeader}>Suggestions</div>
                  {suggestions.map((item, idx) => (
                      <div 
                        key={`${item.type}-${item.id}`}
                        className={`${styles.suggestionItem} ${idx === activeSuggestion ? styles.suggestionItemActive : ''}`}
                        onClick={() => handleSuggestionClick(item)}
                      >
                          <span>{item.name}</span>
                          <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{item.type === 'mountain' ? 'Mountain' : 'Gear'}</span>
                      </div>
                  ))}
              </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="postImage" className={styles.label}>
            {t('postPopup.fields.image')}
          </label>
          <input
            id="postImage"
            type="file"
            accept="image/*"
            className={styles.fileInput}
            onChange={handleFileChange}
            disabled={submitting}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelButton} onClick={onClose} disabled={submitting}>
            {t('postPopup.actions.cancel')}
          </button>
          <button type="submit" className={styles.submitButton} disabled={submitting}>
            {submitting ? t('postPopup.actions.submitting') : t('postPopup.actions.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostPopup;
